/**
 * EditGoodbyeLetterSettingsDialog
 *
 * Per-template configuration for the Lendwise Goodbye Letter (RESPA Notice of
 * Servicing Transfer). Edits the local fields stored on
 * `investor_encompass_template_goodbye_settings`. Synced Encompass contact
 * data is read-only and lives on the `EncompassContactCard` elsewhere.
 */

import { useEffect, useMemo, useState, type RefObject } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';
import {
  getGoodbyeLetterSettings,
  upsertGoodbyeLetterSettings
} from '@/services/goodbyeLetterSettingsService';
import type {
  GoodbyeLetterSettings,
  GoodbyeLetterSettingsInput
} from '@/types/goodbye-letter';
import {
  CONTACT_TYPE_LABELS,
  type EncompassContactType,
  type EncompassTemplateContact
} from '@/types/investor-encompass';
// Shared with the service-side guard so admin entry and back-end validation
// stay locked together. See src/utils/goodbye-letter/url.ts.
import { isSafeUrl, renderUrl } from '@/utils/goodbye-letter/url';
import { resolveGoodbyePaymentWebsite } from '@/utils/goodbye-letter/website';

const log = createComponentLogger('EditGoodbyeLetterSettingsDialog', 'ui');

const ALL_CONTACT_TYPES: EncompassContactType[] = [
  'Payment',
  'CustomerService',
  'Correspondence',
  'NoteDelivery',
  'LoanDelivery',
  'ShipTo',
  'TrailingDocs',
  'MortgageInsurance',
  'Insurance',
  'Assignment',
  'TaxNotice',
  'Secondary'
];

const INSURANCE_DEFAULT_PLACEHOLDER =
  'Leave blank to use the standard RESPA insurance-impact paragraph addressing ' +
  'mortgage life, disability, and other optional insurance. Override only if the ' +
  'investor requires custom language.';

interface FormState {
  paymentContactRole: EncompassContactType;
  customerServiceContactRole: EncompassContactType;
  correspondenceContactRole: EncompassContactType;
  customerServicePhoneOverride: string;
  servicerNameOverride: string;
  correspondenceAttention: string;
  qwrAttention: string;
  onlinePaymentsEnabled: boolean;
  onlinePaymentsUrl: string;
  payByPhoneEnabled: boolean;
  payByPhoneNumber: string;
  autoDeductionEnabled: boolean;
  autoDeductionInstructions: string;
  insuranceImpactStatement: string;
  paServicerCodes: string[];
  propertyStates: string[];
}

const EMPTY_STATE: FormState = {
  paymentContactRole: 'Payment',
  customerServiceContactRole: 'CustomerService',
  correspondenceContactRole: 'Correspondence',
  customerServicePhoneOverride: '',
  servicerNameOverride: '',
  correspondenceAttention: '',
  qwrAttention: '',
  onlinePaymentsEnabled: false,
  onlinePaymentsUrl: '',
  payByPhoneEnabled: false,
  payByPhoneNumber: '',
  autoDeductionEnabled: false,
  autoDeductionInstructions: '',
  paServicerCodes: [],
  propertyStates: [],
  insuranceImpactStatement: ''
};

function settingsToForm(s: GoodbyeLetterSettings): FormState {
  return {
    paymentContactRole: s.paymentContactRole,
    customerServiceContactRole: s.customerServiceContactRole,
    correspondenceContactRole: s.correspondenceContactRole,
    customerServicePhoneOverride: s.customerServicePhoneOverride ?? '',
    servicerNameOverride: s.servicerNameOverride ?? '',
    correspondenceAttention: s.correspondenceAttention ?? '',
    qwrAttention: s.qwrAttention ?? '',
    onlinePaymentsEnabled: s.onlinePaymentsEnabled,
    onlinePaymentsUrl: s.onlinePaymentsUrl ?? '',
    payByPhoneEnabled: s.payByPhoneEnabled,
    payByPhoneNumber: s.payByPhoneNumber ?? '',
    autoDeductionEnabled: s.autoDeductionEnabled,
    autoDeductionInstructions: s.autoDeductionInstructions ?? '',
    paServicerCodes: s.paServicerCodes ?? [],
    propertyStates: s.propertyStates ?? [],
    insuranceImpactStatement: s.insuranceImpactStatement ?? ''
  };
}

function formToInput(f: FormState): GoodbyeLetterSettingsInput {
  // Use `|| null` for text fields so blank strings become null in DB.
  return {
    paymentContactRole: f.paymentContactRole,
    customerServiceContactRole: f.customerServiceContactRole,
    correspondenceContactRole: f.correspondenceContactRole,
    customerServicePhoneOverride: f.customerServicePhoneOverride.trim() || null,
    servicerNameOverride: f.servicerNameOverride.trim() || null,
    correspondenceAttention: f.correspondenceAttention.trim() || null,
    qwrAttention: f.qwrAttention.trim() || null,
    onlinePaymentsEnabled: f.onlinePaymentsEnabled,
    onlinePaymentsUrl: f.onlinePaymentsUrl.trim() || null,
    payByPhoneEnabled: f.payByPhoneEnabled,
    payByPhoneNumber: f.payByPhoneNumber.trim() || null,
    autoDeductionEnabled: f.autoDeductionEnabled,
    autoDeductionInstructions: f.autoDeductionInstructions.trim() || null,
    paServicerCodes: f.paServicerCodes,
    propertyStates: f.propertyStates,
    insuranceImpactStatement: f.insuranceImpactStatement.trim() || null
  };
}

interface PayByPhoneFallback {
  number: string;
  sourceRole: EncompassContactType;
}

interface PayByPhonePrintPreview {
  number: string;
  source: 'manual' | 'contact';
  sourceRole?: EncompassContactType;
}

function contactPhoneForRole(
  contacts: EncompassTemplateContact[],
  role: EncompassContactType
): string {
  return contacts.find((c) => c.contactType === role)?.phone?.trim() ?? '';
}

function resolvePayByPhoneContactFallback(
  contacts: EncompassTemplateContact[],
  paymentRole: EncompassContactType,
  customerServiceRole: EncompassContactType
): PayByPhoneFallback | null {
  const paymentPhone = contactPhoneForRole(contacts, paymentRole);
  if (paymentPhone) {
    return { number: paymentPhone, sourceRole: paymentRole };
  }

  const customerServicePhone = contactPhoneForRole(contacts, customerServiceRole);
  if (customerServicePhone) {
    return { number: customerServicePhone, sourceRole: customerServiceRole };
  }

  return null;
}

function resolvePayByPhonePrintPreview(
  form: FormState,
  fallback: PayByPhoneFallback | null
): PayByPhonePrintPreview | null {
  if (form.payByPhoneEnabled && form.payByPhoneNumber.trim()) {
    return {
      number: form.payByPhoneNumber.trim(),
      source: 'manual'
    };
  }

  if (fallback) {
    return {
      number: fallback.number,
      source: 'contact',
      sourceRole: fallback.sourceRole
    };
  }

  return null;
}

interface EditGoodbyeLetterSettingsDialogProps {
  servicerSelectionMode?: 'product_scope' | 'per_loan';
  open: boolean;
  returnFocusRef?: RefObject<HTMLElement>;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  /** Contact_types that exist on this template (drives the warning chip). */
  availableContactTypes: EncompassContactType[];
  contacts?: EncompassTemplateContact[];
  onSaved?: (settings: GoodbyeLetterSettings) => void;
}

export function EditGoodbyeLetterSettingsDialog({
  open,
  returnFocusRef,
  onOpenChange,
  templateId,
  availableContactTypes,
  contacts = [],
  onSaved,
  servicerSelectionMode = 'product_scope'
}: EditGoodbyeLetterSettingsDialogProps): JSX.Element {
  const [codeDraft, setCodeDraft] = useState('');
  const addCode = () => {
    const codes = codeDraft.split(',').map(code => code.trim().toUpperCase()).filter(Boolean);
    setForm(current => ({ ...current, paServicerCodes: [...new Set([...current.paServicerCodes, ...codes])] }));
    setCodeDraft('');
  };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset + load on open
  useEffect(() => {
    if (!open) {
return;
}
    setErrors({});
    setLoading(true);
    getGoodbyeLetterSettings(templateId)
      .then((s) => setForm(s ? settingsToForm(s) : EMPTY_STATE))
      .catch((err) => {
        log.error('Failed to load goodbye-letter settings', { templateId }, err);
        toast.error('Could not load Goodbye Letter Settings');
      })
      .finally(() => setLoading(false));
  }, [open, templateId]);

  const availableSet = useMemo(
    () => new Set(availableContactTypes),
    [availableContactTypes]
  );

  const payByPhoneFallback = useMemo(
    () => resolvePayByPhoneContactFallback(
      contacts,
      form.paymentContactRole,
      form.customerServiceContactRole
    ),
    [contacts, form.paymentContactRole, form.customerServiceContactRole]
  );
  const payByPhonePreview = useMemo(
    () => resolvePayByPhonePrintPreview(form, payByPhoneFallback),
    [form, payByPhoneFallback]
  );
  const paymentWebsite = contacts.find((contact) => contact.contactType === form.paymentContactRole)?.website;
  const onlineWebsite = resolveGoodbyePaymentWebsite(form, paymentWebsite);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((e) => {
      if (!(key in e)) {
return e;
}
      const next = { ...e };
      delete next[key as string];
      return next;
    });
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (form.onlinePaymentsEnabled) {
      const url = onlineWebsite.url;
      if (!url) {
        e.onlinePaymentsUrl = 'URL required when Online Payments is enabled. Add the Payment website in Encompass and sync, or enter a manual override.';
      } else if (!isSafeUrl(url)) {
        e.onlinePaymentsUrl =
          'Enter a domain like www.example.com or a full https:// URL.';
      }
    }
    if (form.payByPhoneEnabled && !form.payByPhoneNumber.trim() && !payByPhoneFallback) {
      e.payByPhoneNumber =
        'Enter a manual phone number or add a phone to the configured Payment or Customer Service contact.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave(): Promise<void> {
    if (!validate()) {
return;
}
    setSaving(true);
    try {
      const saved = await upsertGoodbyeLetterSettings(templateId, formToInput(form));
      toast.success('Goodbye Letter Settings saved');
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      log.error('Failed to save goodbye-letter settings', { templateId }, err as Error);
      toast.error('Could not save Goodbye Letter Settings');
    } finally {
      setSaving(false);
    }
  }

  const roleSelect = (
    label: string,
    field: 'paymentContactRole' | 'customerServiceContactRole' | 'correspondenceContactRole',
    help: string
  ): JSX.Element => {
    const value = form[field];
    const missingRow = !availableSet.has(value);
    return (
      <div className="grid grid-cols-1 gap-1">
        <Label htmlFor={field}>{label}</Label>
        <Select
          value={value}
          onValueChange={(v) => setField(field, v as EncompassContactType)}
        >
          <SelectTrigger id={field}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ALL_CONTACT_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {CONTACT_TYPE_LABELS[t]}
                {!availableContactTypes.includes(t) ? ' (not on template)' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{help}</p>
        {missingRow && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              No <strong>{CONTACT_TYPE_LABELS[value]}</strong> contact exists on this template.
              The letter will fail to generate until you pick a configured contact type or add
              the missing row in Encompass and resync.
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] grid-cols-1 overflow-y-auto [&>*]:min-w-0"
        onCloseAutoFocus={(event) => {
          if (returnFocusRef?.current?.isConnected) {
            event.preventDefault();
            returnFocusRef.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Goodbye Letter Settings</DialogTitle>
          <DialogDescription>
            Configure per-investor data used by the RESPA Notice of Servicing Transfer letter.
            Synced Encompass contact data (names, addresses, base phone) is read-only and lives
            on the contact cards above.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {servicerSelectionMode === 'per_loan' && (
              <section className="space-y-3" aria-labelledby="servicer-suggestion-title">
                <h3 id="servicer-suggestion-title" className="text-sm font-semibold">Servicer suggestion (Banker Recon)</h3>
                <p className="text-xs text-muted-foreground">Used to pre-select this servicer on the Banker Recon. The person finalizing still confirms.</p>
                <Label htmlFor="pa-servicer-codes">Purchase advice servicer code(s)</Label>
                <div className="flex flex-wrap gap-2">
                  {form.paServicerCodes.map(code => <Button key={code} type="button" variant="outline" size="sm" aria-label={`Remove ${code}`} onClick={() => setField('paServicerCodes', form.paServicerCodes.filter(c => c !== code))}>{code} ×</Button>)}
                </div>
                <Input id="pa-servicer-codes" value={codeDraft} onChange={e => setCodeDraft(e.target.value.toUpperCase())} onBlur={addCode} onKeyDown={e => {
 if (e.key === 'Enter' || e.key === ',') {
 e.preventDefault(); addCode();
}
}} />
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">Property states</legend>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' ').map(state => (
                      <label key={state} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-xs">
                        <Checkbox
                          checked={form.propertyStates.includes(state)}
                          onCheckedChange={checked => {
                            const next = new Set(form.propertyStates);
                            if (checked === true) {
                              next.add(state);
                            } else {
                              next.delete(state);
                            }
                            setField('propertyStates', Array.from(next).sort());
                          }}
                        />
                        <span>{state}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </section>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Contact Role Mapping</h3>
              {roleSelect(
                'Payments mailing',
                'paymentContactRole',
                'Which contact_type fills the "Loan Payments only" mailing row.'
              )}
              {roleSelect(
                'Customer service',
                'customerServiceContactRole',
                'Provides the customer-service phone and the address and Contact line for All Other Correspondence.'
              )}
              {roleSelect(
                'Correspondence / QWR',
                'correspondenceContactRole',
                'Provides the address and Contact line for Notices of Error and Requests for Information. A complete address is required.'
              )}
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Servicer Overrides</h3>
              <div className="grid grid-cols-1 gap-1">
                <Label htmlFor="servicerNameOverride">Servicer name override</Label>
                <Input
                  id="servicerNameOverride"
                  value={form.servicerNameOverride}
                  onChange={(e) => setField('servicerNameOverride', e.target.value)}
                  placeholder="Leave blank to use the Payment contact's name"
                />
              </div>
              <div className="grid grid-cols-1 gap-1">
                <Label htmlFor="correspondenceAttention">All Other Correspondence Attn: override</Label>
                <Input
                  id="correspondenceAttention"
                  value={form.correspondenceAttention}
                  onChange={(e) => setField('correspondenceAttention', e.target.value)}
                  placeholder="Optional manual override"
                  aria-describedby="correspondenceAttention-help"
                />
              </div>
              <p id="correspondenceAttention-help" className="text-xs text-muted-foreground">
                Leave blank to print the synced Customer Service Contact line once.
              </p>
              <div className="grid grid-cols-1 gap-1">
                <Label htmlFor="qwrAttention">QWR Attn: override</Label>
                <Input
                  id="qwrAttention"
                  value={form.qwrAttention}
                  onChange={(e) => setField('qwrAttention', e.target.value)}
                  placeholder="Optional manual override"
                  aria-describedby="qwrAttention-help"
                />
              </div>
              <p id="qwrAttention-help" className="text-xs text-muted-foreground">
                Leave blank to print the synced Correspondence Contact line once.
              </p>
              <div className="grid grid-cols-1 gap-1">
                <Label htmlFor="customerServicePhoneOverride">
                  Customer service phone override
                </Label>
                <Input
                  id="customerServicePhoneOverride"
                  value={form.customerServicePhoneOverride}
                  onChange={(e) => setField('customerServicePhoneOverride', e.target.value)}
                  placeholder="Optional — leave blank to use the synced CustomerService phone"
                />
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Payment Options</h3>
              <p className="text-xs text-muted-foreground">
                Online Payments uses the synced Payment website unless a manual URL overrides it.
                Automatic Deduction uses local settings. Pay-by-Phone
                prints automatically when the configured Payment or Customer Service contact
                has a phone; use a manual override only to print a different number.
              </p>

              {/* Online Payments */}
              <div className="rounded-md border p-3 space-y-2">
                <label className="flex items-center gap-2 font-medium text-sm">
                  <Checkbox
                    checked={form.onlinePaymentsEnabled}
                    onCheckedChange={(v) => setField('onlinePaymentsEnabled', v === true)}
                  />
                  Online Payments
                </label>
                {form.onlinePaymentsEnabled && (
                  <div className="grid grid-cols-1 gap-1">
                    <Label htmlFor="onlinePaymentsUrl" className="text-xs">URL</Label>
                    <Input
                      id="onlinePaymentsUrl"
                      value={form.onlinePaymentsUrl}
                      onChange={(e) => setField('onlinePaymentsUrl', e.target.value)}
                      placeholder="Optional manual override"
                      aria-describedby={`onlinePaymentsUrl-help${errors.onlinePaymentsUrl ? ' onlinePaymentsUrl-error' : ''}`}
                      aria-invalid={!!errors.onlinePaymentsUrl}
                    />
                    <p id="onlinePaymentsUrl-help" className="text-xs text-muted-foreground">
                      {onlineWebsite.source === 'encompass' && onlineWebsite.url && isSafeUrl(onlineWebsite.url) ? (
                        <>Automatic from Encompass ({CONTACT_TYPE_LABELS[form.paymentContactRole]}):{' '}
                          <a className="underline break-all" href={renderUrl(onlineWebsite.url)} target="_blank" rel="noopener noreferrer">
                            {onlineWebsite.url}
                          </a>. Updates after each template sync.</>
                      ) : (
                        <>Leave blank to use the synced {CONTACT_TYPE_LABELS[form.paymentContactRole]} website. A manual URL overrides Encompass.</>
                      )}
                    </p>
                    {form.onlinePaymentsUrl.trim() && paymentWebsite && isSafeUrl(paymentWebsite.trim()) && (
                      <Button type="button" variant="ghost" size="sm" className="justify-self-start" onClick={() => setField('onlinePaymentsUrl', '')}>
                        Use Encompass automatically
                      </Button>
                    )}
                    {errors.onlinePaymentsUrl && (
                      <p id="onlinePaymentsUrl-error" className="text-xs text-destructive">{errors.onlinePaymentsUrl}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Pay by Phone */}
              <div className="rounded-md border p-3 space-y-2">
                <label className="flex items-center gap-2 font-medium text-sm">
                  <Checkbox
                    checked={form.payByPhoneEnabled}
                    onCheckedChange={(v) => setField('payByPhoneEnabled', v === true)}
                  />
                  Use manual Pay-by-Phone number
                </label>
                {payByPhonePreview ? (
                  <p className="text-xs text-muted-foreground">
                    {payByPhonePreview.source === 'manual' ? (
                      <>
                        Will print the manual number:{' '}
                        <span className="font-medium">{payByPhonePreview.number}</span>
                      </>
                    ) : (
                      <>
                        Will print from {CONTACT_TYPE_LABELS[payByPhonePreview.sourceRole!]} contact:{' '}
                        <span className="font-medium">{payByPhonePreview.number}</span>
                      </>
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No configured Payment or Customer Service contact phone is available.
                    Add a manual number to print Pay-by-Phone.
                  </p>
                )}
                {form.payByPhoneEnabled && (
                  <div className="grid grid-cols-1 gap-1">
                    <Label htmlFor="payByPhoneNumber" className="text-xs">
                      Manual phone number
                    </Label>
                    <Input
                      id="payByPhoneNumber"
                      value={form.payByPhoneNumber}
                      onChange={(e) => setField('payByPhoneNumber', e.target.value)}
                      placeholder={payByPhoneFallback?.number ?? '1-866-555-0000'}
                    />
                    {errors.payByPhoneNumber && (
                      <p className="text-xs text-destructive">{errors.payByPhoneNumber}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Automatic Deduction */}
              <div className="rounded-md border p-3 space-y-2">
                <label className="flex items-center gap-2 font-medium text-sm">
                  <Checkbox
                    checked={form.autoDeductionEnabled}
                    onCheckedChange={(v) => setField('autoDeductionEnabled', v === true)}
                  />
                  Automatic Deduction
                </label>
                {form.autoDeductionEnabled && (
                  <div className="grid grid-cols-1 gap-1">
                    <Label htmlFor="autoDeductionInstructions" className="text-xs">
                      Custom instructions (optional)
                    </Label>
                    <Textarea
                      id="autoDeductionInstructions"
                      value={form.autoDeductionInstructions}
                      onChange={(e) =>
                        setField('autoDeductionInstructions', e.target.value)
                      }
                      placeholder="Leave blank to render the standard 'contact Customer Service' boilerplate."
                      rows={3}
                    />
                  </div>
                )}
              </div>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold">Insurance-impact statement (optional)</h3>
              <p className="text-xs text-muted-foreground">{INSURANCE_DEFAULT_PLACEHOLDER}</p>
              <Textarea
                value={form.insuranceImpactStatement}
                onChange={(e) => setField('insuranceImpactStatement', e.target.value)}
                rows={4}
                placeholder="Custom insurance-impact paragraph (overrides the default)"
              />
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving || loading}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
